/**
 * 데이터처리(transformLoad) 노드의 **분할처리** — 고른 방식에 따라 세부 흐름을 자동으로 만들어 준다.
 *
 * 왜 자동 생성인가: 대용량을 나눠 처리하는 모양(분기 → 파티션별 추출 → 합류 → 적재, 임시 테이블 + DB link,
 * 중간 파일 경유 …)은 노드 열댓 개짜리 정형 패턴이다. 손으로 그리면 파티션 조건 하나만 어긋나도 데이터가
 * 새거나 겹친다. 그래서 **방식만 고르면** 같은 모양을 늘 같은 규칙으로 만들고, 결과는 읽기 전용 그룹으로 묶어
 * 사람이 안쪽을 흔들지 못하게 한다(흔들면 그 규칙이 깨지므로).
 *
 * 고를 수 있는 방식은 소스·타깃이 무엇이냐에 달려 있다.
 *   - 소스가 파일(SAM): 지금 실행기는 파일을 구간으로 나눠 읽지 못한다 → 행 분할은 제공하지 않는다.
 *   - 소스가 DB테이블: 키로 나눠 병렬 추출할 수 있다.
 *   - 소스·타깃이 **같은 연결**: 데이터를 꺼내지 않고 DB 안에서 INSERT … SELECT 한 번이 가장 빠르다.
 *   - **같은 종류의 다른 연결**(PG→PG 등): DB link 로 임시 분할 테이블을 만들어 옮길 수 있다.
 *   - **다른 종류**(PG→Oracle 등): DB link 가 없으므로 중간 파일을 거쳐 벌크로 적재한다.
 */
import type { Endpoint } from './transformLoad';
import i18n, { SUPPORTED_LANGUAGES } from '../../../i18n';

const tr = (key: string, opts?: Record<string, unknown>) => String(i18n.t(`workflow:split.${key}`, opts as any));

export type SplitMode = 'none' | 'range' | 'sql-inplace' | 'dblink' | 'staging-file';

export interface SplitSpec {
  mode: SplitMode;
  /** 분할 수 (2~32) */
  count: number;
  /** 분할 기준 컬럼 — 숫자형이면 MOD, 아니면 해시로 나눈다 */
  keyColumn: string;
  /** DB link 이름 (dblink 방식) */
  dbLinkName?: string;
  /** 임시 분할 테이블 접두어 (dblink 방식) */
  tempPrefix?: string;
  /** 중간 파일 경로 (staging-file 방식) */
  stagingPath?: string;
  /** 적재에 벌크 로더를 쓸지 (staging-file·range 방식에서 타깃이 DB일 때) */
  useBulkLoader?: boolean;
}

export const DEFAULT_SPLIT: SplitSpec = { mode: 'none', count: 8, keyColumn: '', tempPrefix: 'of_tmp', useBulkLoader: false };

/** 소스·타깃 관계 — 고를 수 있는 방식을 정한다 */
export interface SplitContext {
  srcKind: 'sam' | 'db' | 'inner' | 'none';
  tgtKind: 'sam' | 'db' | 'inner' | 'none';
  /** 둘 다 DB 일 때의 관계 */
  dbRelation: 'same' | 'homogeneous' | 'heterogeneous' | 'n/a';
  srcConnection?: string;
  tgtConnection?: string;
  srcType?: string;
  tgtType?: string;
  srcTable?: string;
  tgtTable?: string;
  srcColumns: string[];
}

const kindOf = (ep: Endpoint | null): SplitContext['srcKind'] => (ep ? (ep.kind as any) : 'none');

/** 연결 id → DBMS 종류 (postgres·mysql·oracle …) */
const typeOf = (connections: Array<{ connection_id: string; conn_type?: string }>, id?: string) =>
  (connections.find((c) => c.connection_id === id)?.conn_type || '').toLowerCase();

export function splitContext(
  source: Endpoint | null, target: Endpoint | null,
  connections: Array<{ connection_id: string; conn_type?: string }>,
): SplitContext {
  const srcCfg = source?.node?.data?.config || {};
  const tgtCfg = target?.node?.data?.config || {};
  const srcKind = kindOf(source);
  const tgtKind = kindOf(target);
  const srcConnection = srcKind === 'db' ? srcCfg.connection : undefined;
  const tgtConnection = tgtKind === 'db' ? tgtCfg.connection : undefined;
  const srcType = typeOf(connections, srcConnection);
  const tgtType = typeOf(connections, tgtConnection);

  let dbRelation: SplitContext['dbRelation'] = 'n/a';
  if (srcKind === 'db' && tgtKind === 'db') {
    if (srcConnection && srcConnection === tgtConnection) dbRelation = 'same';
    else if (srcType && srcType === tgtType) dbRelation = 'homogeneous';
    else dbRelation = 'heterogeneous';
  }

  const qualified = (cfg: any) => (cfg.table ? `${cfg.schema ? `${cfg.schema}.` : ''}${cfg.table}` : '');
  return {
    srcKind, tgtKind, dbRelation, srcConnection, tgtConnection, srcType, tgtType,
    srcTable: qualified(srcCfg), tgtTable: qualified(tgtCfg),
    srcColumns: (srcCfg.tableColumns || []).map((c: any) => c?.name).filter(Boolean),
  };
}

export interface SplitModeInfo {
  mode: SplitMode;
  label: string;
  /** 무엇을 하는 방식인지 — 화면에 그대로 보여 준다 */
  desc: string;
  /** 고를 수 없으면 그 이유 (버튼을 끄고 이유를 붙인다) */
  blocked?: string;
  needs: Array<'count' | 'keyColumn' | 'dbLinkName' | 'tempPrefix' | 'stagingPath' | 'useBulkLoader'>;
}

/** 이 소스·타깃 조합에서 고를 수 있는 분할 방식 — 못 고르는 것도 이유와 함께 남긴다 */
export function splitModesFor(ctx: SplitContext): SplitModeInfo[] {
  const fileSource = ctx.srcKind === 'sam';
  const innerSource = ctx.srcKind === 'inner';
  const noSource = ctx.srcKind === 'none' || ctx.tgtKind === 'none';

  const rangeBlocked = noSource ? tr('connectFirst')
    : fileSource ? tr('fileNoRange')
      : innerSource ? tr('innerNoRange') : undefined;

  return [
    {
      mode: 'none', label: tr('none'), desc: tr('noneDesc'), needs: [],
    },
    {
      mode: 'range',
      label: tr('range'),
      desc: tr('rangeDesc'),
      blocked: rangeBlocked,
      needs: ['count', 'keyColumn'],
    },
    {
      mode: 'sql-inplace',
      label: tr('inplace'),
      desc: tr('inplaceDesc'),
      blocked: ctx.dbRelation === 'same' ? undefined : tr('sameOnly'),
      needs: [],
    },
    {
      mode: 'dblink',
      label: tr('dblink'),
      desc: tr('dblinkDesc'),
      blocked: ctx.dbRelation === 'homogeneous' ? undefined
        : ctx.dbRelation === 'same' ? tr('sameBetter')
          : ctx.dbRelation === 'heterogeneous' ? tr('heteroNoLink')
            : tr('dbToDbOnly'),
      needs: ['count', 'keyColumn', 'dbLinkName', 'tempPrefix'],
    },
    {
      mode: 'staging-file',
      label: tr('staging'),
      desc: tr('stagingDesc'),
      blocked: noSource ? tr('connectFirst')
        : ctx.srcKind !== 'db' ? tr('dbSourceOnly')
          : ctx.tgtKind !== 'db' ? tr('dbTargetOnly') : undefined,
      needs: ['count', 'keyColumn', 'stagingPath', 'useBulkLoader'],
    },
  ];
}

// ---------------------------------------------------------------- 흐름 생성

export interface BuiltFlow {
  /** 읽기 전용 그룹 + 그 안의 노드들 */
  nodes: any[];
  edges: any[];
  /** 바깥에서 들어오는 엣지가 붙을 노드 id (소스 데이터 노드 → 이것) */
  entryId: string;
  /** 바깥으로 나가는 엣지가 붙을 노드 id (이것 → 타깃 데이터 노드) */
  exitId: string;
  /** 사람이 읽을 요약 — 그룹 이름 아래에 남긴다 */
  summary: string;
}

const GAP_Y = 90;
const COL = 150;

let seq = 0;
const nid = (p: string) => `${p}_${Date.now().toString(36)}_${(seq += 1).toString(36)}`;

const mk = (id: string, type: string, label: string, x: number, y: number, config: any = {}, parentId?: string) => ({
  id, type,
  position: { x, y },
  data: { label, config, readOnly: true },
  ...(parentId ? { parentId, extent: 'parent' as const, expandParent: true } : {}),
  draggable: false,
  deletable: false,
  className: 'workflow-child-node',
  style: { width: 105, height: 56 },
});

const link = (source: string, target: string, sourceHandle?: string) => ({
  id: `e_${source}_${target}${sourceHandle ? `_${sourceHandle}` : ''}`,
  source, target, ...(sourceHandle ? { sourceHandle } : {}),
  type: 'workflow', deletable: false,
});

/** 파티션 조건 — 숫자 키면 MOD, 아니면 해시. 종류별 문법이 달라 소스 DBMS 로 고른다 */
function partitionWhere(dbType: string, key: string, count: number, k: number): string {
  const t = (dbType || '').toLowerCase();
  // 티베로는 오라클 호환이라 ORA_HASH 를 그대로 쓴다(없는 판올림이면 생성된 SQL 을 손으로 고칠 수 있다)
  if (t.includes('oracle') || t.includes('tibero')) return `MOD(ORA_HASH(${key}), ${count}) = ${k}`;
  if (t.includes('mysql')) return `MOD(CRC32(CAST(${key} AS CHAR)), ${count}) = ${k}`;
  // SQLite 에는 해시 함수가 없다 — 키의 길이·마지막 글자로 고르게 나눌 수 없으므로, 숫자로 바꿔 나머지를 쓴다.
  // (무설치판의 샘플 DB 가 SQLite 다. 키가 숫자가 아니면 CAST 가 0 이 되어 한 조각에 몰리므로 그때는 쓰지 않는다)
  if (t.includes('sqlite')) return `ABS(CAST(${key} AS INTEGER)) % ${count} = ${k}`;
  return `MOD(ABS(HASHTEXT(${key}::text)), ${count}) = ${k}`;   // PostgreSQL
}

/**
 * 고른 방식대로 노드·엣지를 만든다. 모든 노드는 읽기 전용 그룹의 자식이 되고, 그룹 자신도 읽기 전용이다.
 * 원래 데이터처리 노드의 설정(변환·적재 방식)은 마지막 적재 노드가 그대로 물려받는다.
 */
export function buildSplitFlow(spec: SplitSpec, ctx: SplitContext, baseConfig: any, groupLabel: string): BuiltFlow {
  const groupId = nid('split');
  const n = Math.max(2, Math.min(32, Number(spec.count) || 8));
  const key = (spec.keyColumn || '').trim();
  const cols = ctx.srcColumns.length ? ctx.srcColumns.join(', ') : '*';
  const nodes: any[] = [];
  const edges: any[] = [];
  let entryId = '';
  let exitId = '';
  let summary = '';

  const loadConfig = { ...baseConfig, split: undefined };   // 적재 노드는 원래 설정을 따른다 (분할 설정은 그룹이 들고 있다)

  if (spec.mode === 'sql-inplace') {
    const sql = nid('sql');
    const stmt = `INSERT INTO ${ctx.tgtTable} (${cols})\nSELECT ${cols} FROM ${ctx.srcTable};`;
    nodes.push(mk(sql, 'sqlScript', tr('node.inplace'), 40, 40, {
      connection: ctx.srcConnection || '', sql: (baseConfig?.mode === 'WRITE' ? `TRUNCATE TABLE ${ctx.tgtTable};\n` : '') + stmt,
      fetchResult: false,
    }, groupId));
    entryId = sql; exitId = sql;
    summary = tr('summary.inplace');
  } else if (spec.mode === 'range' || spec.mode === 'staging-file') {
    const br = nid('br');
    nodes.push(mk(br, 'branch', tr('node.split', { n }), 40, 40 + ((n - 1) * GAP_Y) / 2, { condition: '', defaultPath: 'TRUE' }, groupId));
    entryId = br;
    const mg = nid('mg');
    for (let k = 0; k < n; k++) {
      const y = 40 + k * GAP_Y;
      const p = nid(`p${k}`);
      nodes.push(mk(p, 'transformLoad', tr('node.part', { k }), 40 + COL, y, {
        transformMode: 'MOVE', mapping: [], mode: 'WRITE', useBulkLoader: false,
        extractMode: 'QUERY',
        query: `SELECT ${cols} FROM ${ctx.srcTable} WHERE ${partitionWhere(ctx.srcType || '', key || '1', n, k)}`,
      }, groupId));
      const d = nid(`d${k}`);
      nodes.push(mk(d, 'innerData', tr('node.partData', { k }), 40 + COL * 2, y, {}, groupId));
      edges.push(link(br, p), link(p, d), link(d, mg));
    }
    const midY = 40 + ((n - 1) * GAP_Y) / 2;
    nodes.push(mk(mg, 'merge', tr('node.merge'), 40 + COL * 3, midY, { joinType: 'ALL' }, groupId));

    if (spec.mode === 'range') {
      const load = nid('load');
      nodes.push(mk(load, 'transformLoad', tr('node.load'), 40 + COL * 4, midY, loadConfig, groupId));
      edges.push(link(mg, load));
      exitId = load;
      summary = tr('summary.range', { key: key || tr('unset'), n });
    } else {
      // 중간 파일 경유: 합류 결과를 파일로 내리고, 그 파일을 다시 읽어 벌크로 적재한다
      const toFile = nid('tofile');
      const file = nid('file');
      const load = nid('load');
      const path = spec.stagingPath || `out/staging_${(ctx.tgtTable || 'target').replace(/[^\w]+/g, '_')}.csv`;
      nodes.push(mk(toFile, 'transformLoad', tr('node.toFile'), 40 + COL * 4, midY, {
        transformMode: 'MOVE', mapping: [], mode: 'WRITE', useBulkLoader: false,
      }, groupId));
      nodes.push(mk(file, 'csvData', tr('node.file'), 40 + COL * 5, midY, {
        filepath: path, delimiter: ',', hasHeader: true, encoding: 'UTF-8', mode: 'WRITE',
      }, groupId));
      nodes.push(mk(load, 'transformLoad', tr('node.bulkLoad'), 40 + COL * 6, midY, {
        ...loadConfig, useBulkLoader: spec.useBulkLoader !== false,
      }, groupId));
      edges.push(link(mg, toFile), link(toFile, file), link(file, load));
      exitId = load;
      summary = tr('summary.staging', { key: key || tr('unset'), n, path });
    }
  } else if (spec.mode === 'dblink') {
    // 소스 쪽에서 임시 분할 테이블 N개를 만들고, DB link 로 타깃에 옮긴 뒤 합친다.
    const link_ = spec.dbLinkName || 'TGT_LINK';
    const prefix = spec.tempPrefix || 'of_tmp';
    const prep = nid('prep');
    nodes.push(mk(prep, 'sqlScript', tr('node.prep'), 40, 40, {
      connection: ctx.srcConnection || '', fetchResult: false,
      sql: Array.from({ length: n }, (_, k) =>
        `DROP TABLE IF EXISTS ${prefix}_${k};\nCREATE TABLE ${prefix}_${k} AS SELECT ${cols} FROM ${ctx.srcTable} WHERE ${partitionWhere(ctx.srcType || '', key || '1', n, k)};`).join('\n'),
    }, groupId));
    entryId = prep;
    const mg = nid('mg');
    for (let k = 0; k < n; k++) {
      const y = 40 + k * GAP_Y;
      const mv = nid(`mv${k}`);
      nodes.push(mk(mv, 'sqlScript', tr('node.move', { k }), 40 + COL, y, {
        connection: ctx.srcConnection || '', fetchResult: false,
        sql: `INSERT INTO ${ctx.tgtTable}@${link_} (${cols}) SELECT ${cols} FROM ${prefix}_${k};`,
      }, groupId));
      const d = nid(`d${k}`);
      nodes.push(mk(d, 'innerData', tr('node.partResult', { k }), 40 + COL * 2, y, {}, groupId));
      edges.push(link(prep, mv), link(mv, d), link(d, mg));
    }
    const midY = 40 + ((n - 1) * GAP_Y) / 2;
    nodes.push(mk(mg, 'merge', tr('node.merge'), 40 + COL * 3, midY, { joinType: 'ALL' }, groupId));
    const cleanup = nid('cleanup');
    nodes.push(mk(cleanup, 'sqlScript', tr('node.cleanup'), 40 + COL * 4, midY, {
      connection: ctx.srcConnection || '', fetchResult: false,
      sql: Array.from({ length: n }, (_, k) => `DROP TABLE IF EXISTS ${prefix}_${k};`).join('\n'),
    }, groupId));
    edges.push(link(mg, cleanup));
    exitId = cleanup;
    summary = tr('summary.dblink', { prefix, last: n - 1, link: link_ });
  }

  // 그룹 — 자식들을 감싸는 읽기 전용 상자. 크기는 자식 배치에 맞춰 넉넉히 잡는다
  const maxX = Math.max(...nodes.map((x) => x.position.x + 105), 300);
  const maxY = Math.max(...nodes.map((x) => x.position.y + 56), 160);
  const group = {
    id: groupId, type: 'group',
    position: { x: 0, y: 0 },
    data: {
      label: groupLabel,
      readOnly: true,
      autoSize: false,
      /** 이 그룹이 무엇으로 만들어졌는지 — 속성 패널이 이것으로 편집·해제를 제공한다 */
      split: { ...spec, count: n },
      splitSummary: summary,
      config: { ...baseConfig },
    },
    style: { width: maxX + 40, height: maxY + 60 },
    deletable: false,
  };
  return { nodes: [group, ...nodes], edges, entryId, exitId, summary };
}

/** 분할 그룹 이름에서 '(분할처리)' 접미어를 뗀다 — 어느 지원 언어로 붙였든 */
export function stripSplitSuffix(label: string): string {
  const suffixes = SUPPORTED_LANGUAGES.map((l) => String(i18n.getFixedT(l.code)('workflow:split.groupSuffix')));
  const trimmed = label.trim();
  const hit = suffixes.find((sfx) => trimmed.endsWith(sfx));
  return hit ? trimmed.slice(0, -hit.length).trim() : trimmed;
}
